/**
 * Tag Generation Service
 * API client for individual lesson tag generation
 */

import apiClient from './api';
import { Product, ExperimentVersion, TagCandidate } from '../types';

// ============================================================================
// Types
// ============================================================================

export interface SingleTagGenerationResult {
  success: boolean;
  tagIds: string[];
  mathExpressionTags?: any[]; // SPINE format
  error?: string;
}

export interface TagGenerationRequest {
  product: Product;
  experiment: ExperimentVersion;
  predictionId: string;
}

export interface SaveAndGenerateResult {
  saveSuccess: boolean;
  generationResult: SingleTagGenerationResult;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Generate SPINE tags for a single lesson
 */
export const generateTagsForLesson = async (
  product: Product,
  experiment: ExperimentVersion,
  predictionId: string
): Promise<SingleTagGenerationResult> => {
  const response = await apiClient.post<SingleTagGenerationResult>('/tags/generate/single', {
    product,
    experiment,
    predictionId,
  });

  return response.data;
};

/**
 * Save prediction data and generate SPINE tags for a single lesson
 */
export const saveAndGenerateTagsForLesson = async (
  product: Product,
  experiment: ExperimentVersion,
  predictionId: string,
  predictionData: TagCandidate[]
): Promise<SaveAndGenerateResult> => {
  const response = await apiClient.post<SaveAndGenerateResult>('/tags/save-and-generate', {
    product,
    experiment,
    predictionId,
    predictionData,
  });

  return response.data;
};

// ============================================================================
// Tag Details Types
// ============================================================================

export interface SpineTagDetails {
  id: string;
  competencyId: string;
  competency: {
    identifier: string;
    title: string;
    uri: string;
    subjectByUri?: string;
    domainByUri?: string;
    clusterByUri?: string;
  };
  limiterIds: string[];
  limiterGroups: Array<{
    id: string;
    title: string;
    isMath: boolean;
    limiters: Array<{
      id: string;
      title: string;
      limiterType: string;
    }>;
  }>;
  regexTargetComposite?: {
    type: string;
    title: string;
    regexTargets: any[];
  };
  created: string;
}

export interface TagDetailsResponse {
  tags: SpineTagDetails[];
}

/**
 * Fetch detailed tag information from SPINE API via backend
 */
export const getTagDetails = async (tagIds: string[]): Promise<SpineTagDetails[]> => {
  if (!tagIds || tagIds.length === 0) {
    return [];
  }

  const response = await apiClient.post<TagDetailsResponse>('/tags/details', { tagIds });
  return response.data.tags;
};

// ============================================================================
// React Query Keys
// ============================================================================

export const tagGenerationKeys = {
  all: ['tagGeneration'] as const,
  lesson: (predictionId: string) => [...tagGenerationKeys.all, predictionId] as const,
  details: (tagIds: string[]) => [...tagGenerationKeys.all, 'details', tagIds] as const,
};